<template>
  <div class="batch-intake-manager">
    <div class="page-header">
      <h2><i class="fas fa-cloud-upload-alt"></i> COI Batch Intake</h2>
      <p class="subtitle">
        Send a secure upload link, review incoming certificates, then process them into this project.
      </p>
    </div>

    <div v-if="loading && !batches.length" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading intake batches…
    </div>

    <div v-else class="intake-layout">
      <!-- Create link -->
      <div class="config-card">
        <div class="card-header">
          <h3><i class="fas fa-link"></i> Create Upload Link</h3>
        </div>
        <div class="card-body">
          <div class="form-row">
            <div class="form-group">
              <label>Recipient email (optional)</label>
              <input v-model="newBatch.email" type="email" class="form-control" placeholder="vendor@example.com" />
            </div>
            <div class="form-group">
              <label>Link expires (days)</label>
              <input v-model.number="newBatch.expiresDays" type="number" min="1" max="90" class="form-control" />
            </div>
          </div>
          <div class="form-group checkbox-row">
            <label>
              <input v-model="newBatch.skipProcessing" type="checkbox" />
              Defer processing until triggered (recommended for bulk drops)
            </label>
          </div>
          <div class="action-buttons">
            <button class="btn btn-primary" :disabled="creating" @click="createBatch">
              <i class="fas fa-plus"></i>
              {{ creating ? 'Creating…' : 'Create Link' }}
            </button>
          </div>
          <div v-if="lastCreatedUrl" class="created-link">
            <label>Share this link with the client:</label>
            <div class="link-row">
              <input :value="lastCreatedUrl" readonly class="form-control" />
              <button class="btn btn-secondary" @click="copyLink(lastCreatedUrl)">
                <i class="fas fa-copy"></i> Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Batch list -->
      <div class="batches-card">
        <div class="card-header">
          <h3><i class="fas fa-inbox"></i> Intake Batches</h3>
          <button class="btn btn-secondary btn-sm" @click="loadBatches">
            <i class="fas fa-sync" :class="{ 'fa-spin': loading }"></i> Refresh
          </button>
        </div>
        <div class="card-body">
          <div v-if="!batches.length" class="empty-state">
            <p>No intake batches yet. Create a link above to get started.</p>
          </div>
          <div v-else class="batch-list">
            <button
              v-for="batch in batches"
              :key="batch.id"
              type="button"
              class="batch-item"
              :class="{ active: selectedBatchId === batch.id }"
              @click="selectBatch(batch.id)"
            >
              <div class="batch-item-main">
                <span class="batch-date">{{ formatDate(batch.created_at) }}</span>
                <span class="batch-email">{{ batch.email || 'No email on file' }}</span>
              </div>
              <div class="batch-item-stats">
                <span>{{ batch.document_count }} uploaded</span>
                <span>{{ batch.pending_check_count }} pending</span>
                <span v-if="!batch.is_valid" class="badge expired">Expired</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Selected batch detail -->
      <div v-if="selectedBatchId && detail" class="detail-card">
        <div class="card-header">
          <h3><i class="fas fa-folder-open"></i> Batch Detail</h3>
          <div class="header-actions">
            <button class="btn btn-secondary btn-sm" @click="copyLink(detail.audit.upload_url)">
              <i class="fas fa-copy"></i> Copy Link
            </button>
          </div>
        </div>
        <div class="card-body">
          <div class="metrics-grid">
            <div class="metric">
              <div class="metric-label">Uploaded</div>
              <div class="metric-value">{{ detail.audit.document_count }}</div>
            </div>
            <div class="metric">
              <div class="metric-label">Classified</div>
              <div class="metric-value">{{ detail.audit.classified_count }}</div>
            </div>
            <div class="metric">
              <div class="metric-label">Checked</div>
              <div class="metric-value">{{ detail.audit.checked_count }}</div>
            </div>
            <div class="metric">
              <div class="metric-label">Entities linked</div>
              <div class="metric-value">{{ detail.audit.entity_linked_count }}</div>
            </div>
          </div>

          <div v-if="detail.audit.notes && detail.audit.notes.length" class="notes">
            <p v-for="(note, idx) in detail.audit.notes" :key="idx">{{ note }}</p>
          </div>

          <div class="process-options">
            <label>
              <input v-model="processOptions.createEntities" type="checkbox" />
              Create entities &amp; compliance subjects from COI insured names
            </label>
          </div>

          <div class="action-buttons">
            <button
              class="btn btn-outline"
              :disabled="processing"
              @click="runProcess(true)"
            >
              Dry Run
            </button>
            <button
              class="btn btn-primary"
              :disabled="processing || detail.audit.document_count === 0"
              @click="runProcess(false)"
            >
              <i class="fas fa-play"></i>
              {{ processing ? 'Processing…' : 'Process Batch' }}
            </button>
          </div>

          <div v-if="processResult" class="process-result">
            <p>
              Queued {{ processResult.checks_started }} check(s);
              {{ processResult.entities_created }} entities created;
              {{ processResult.entities_linked }} linked.
            </p>
          </div>

          <!-- Document queue -->
          <div class="document-queue">
            <h4>Uploaded Documents</h4>
            <div v-if="!detail.documents.length" class="empty-state small">
              No documents yet — share the upload link with the client.
            </div>
            <table v-else class="doc-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Uploaded</th>
                  <th>Classification</th>
                  <th>Check</th>
                  <th>Subject</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in detail.documents" :key="doc.id">
                  <td class="doc-name">{{ doc.name }}</td>
                  <td>{{ formatDate(doc.created_at) }}</td>
                  <td>{{ doc.classification_status || '—' }}</td>
                  <td>
                    <span v-if="doc.check_run && doc.check_run.status">
                      {{ doc.check_run.overall_result || doc.check_run.status }}
                    </span>
                    <span v-else>Pending</span>
                  </td>
                  <td>{{ doc.compliance_subject_id || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="error-banner">{{ error }}</div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'BatchIntakeManager',
  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      creating: false,
      processing: false,
      error: null,
      batches: [],
      selectedBatchId: null,
      detail: null,
      lastCreatedUrl: '',
      processResult: null,
      newBatch: {
        email: '',
        expiresDays: 14,
        skipProcessing: true,
      },
      processOptions: {
        createEntities: true,
      },
    };
  },
  mounted() {
    this.loadBatches();
  },
  methods: {
    async loadBatches() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get(`/projects/${this.projectId}/intake-batches/`);
        this.batches = response.data.batches || [];
        if (this.selectedBatchId) {
          await this.loadDetail(this.selectedBatchId);
        } else if (this.batches.length) {
          await this.selectBatch(this.batches[0].id);
        }
      } catch (err) {
        this.error = err.response?.data?.error || err.message || 'Failed to load batches';
      } finally {
        this.loading = false;
      }
    },
    async selectBatch(batchId) {
      this.selectedBatchId = batchId;
      this.processResult = null;
      await this.loadDetail(batchId);
    },
    async loadDetail(batchId) {
      try {
        const response = await api.get(
          `/projects/${this.projectId}/intake-batches/${batchId}/`
        );
        this.detail = response.data;
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to load batch detail';
      }
    },
    async createBatch() {
      this.creating = true;
      this.error = null;
      try {
        const response = await api.post(`/projects/${this.projectId}/intake-batches/`, {
          email: this.newBatch.email || null,
          expires_days: this.newBatch.expiresDays,
          skip_processing: this.newBatch.skipProcessing,
        });
        const batch = response.data.batch;
        this.lastCreatedUrl = batch.upload_url;
        await this.loadBatches();
        if (batch.id) {
          await this.selectBatch(batch.id);
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to create upload link';
      } finally {
        this.creating = false;
      }
    },
    async runProcess(dryRun) {
      if (!this.selectedBatchId) return;
      this.processing = true;
      this.error = null;
      try {
        const response = await api.post(
          `/projects/${this.projectId}/intake-batches/${this.selectedBatchId}/process/`,
          {
            create_entities: this.processOptions.createEntities,
            dry_run: dryRun,
          }
        );
        this.processResult = response.data;
        if (!dryRun) {
          await this.loadDetail(this.selectedBatchId);
          await this.loadBatches();
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Processing failed';
      } finally {
        this.processing = false;
      }
    },
    copyLink(url) {
      if (!url) return;
      navigator.clipboard.writeText(url).catch(() => {});
    },
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString();
    },
  },
};
</script>

<style scoped>
.batch-intake-manager {
  margin-top: 1.5rem;
}

.page-header h2 {
  margin: 0 0 0.25rem;
  font-size: 1.35rem;
}

.subtitle {
  color: #64748b;
  margin: 0 0 1.25rem;
}

.intake-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.config-card,
.batches-card,
.detail-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  margin: 0;
  font-size: 1rem;
}

.card-body {
  padding: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 160px;
  gap: 1rem;
}

.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.form-control {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.checkbox-row label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.created-link {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.link-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

.batch-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.batch-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  cursor: pointer;
}

.batch-item.active {
  border-color: #2563eb;
  background: #eff6ff;
}

.batch-item-main {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.batch-date {
  font-weight: 600;
  font-size: 0.9rem;
}

.batch-email {
  font-size: 0.8rem;
  color: #64748b;
}

.batch-item-stats {
  display: flex;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: #475569;
}

.badge.expired {
  color: #b91c1c;
  font-weight: 600;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.metric {
  padding: 0.65rem;
  background: #f8fafc;
  border-radius: 6px;
}

.metric-label {
  font-size: 0.75rem;
  color: #64748b;
}

.metric-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.notes {
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 0.85rem;
}

.process-options {
  margin-bottom: 0.75rem;
}

.process-result {
  margin: 0.75rem 0;
  padding: 0.5rem 0.75rem;
  background: #ecfdf5;
  border-radius: 6px;
  font-size: 0.9rem;
}

.doc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.doc-table th,
.doc-table td {
  padding: 0.45rem 0.5rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.doc-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  color: #64748b;
  text-align: center;
  padding: 1rem;
}

.empty-state.small {
  padding: 0.5rem;
  font-size: 0.85rem;
}

.error-banner {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 6px;
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

@media (max-width: 768px) {
  .form-row,
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
